/**
 * FPS monitor — лёгкий RAF-loop собирающий frame timings и эмитящий
 * `perf.fps_sample` каждые ~10s в backend D1.
 *
 * Сигнал для JB чтобы реально знать сколько FPS в TG bot, какие
 * device classes лагают, и где p95 frame time.
 *
 * Метрики per sample:
 *   - avg_fps — среднее за окно
 *   - p95_frame_ms — 95-й перцентиль frame time (выявляет stutters)
 *   - slow_frames — count кадров >50ms (= <20fps) за окно
 *   - sample_seconds — длина окна (~10)
 *   - is_mobile + screen_w/h — device class для cross-correlation
 *
 * Дополнительно: одноразовый emit `perf.slow_frame` на каждый кадр
 * > 200ms (серьёзный stutter), throttle'нутый до раз/2s чтобы burst
 * stutter'ов не флудил telemetry.
 *
 * Идемпотентен — `installFpsMonitor()` повторно вызвать = no-op.
 * Pause when document.hidden (visibilitychange) — не считаем фейковые
 * "лаги" когда вкладка не активна (browser throttle'нет RAF до 1fps).
 */
import { emit } from './emit.js';

const SAMPLE_WINDOW_MS = 10_000;
/** Frame time выше = "медленный кадр" (счётчик). */
const SLOW_FRAME_THRESHOLD_MS = 50;
/** Frame time выше = одноразовый "stutter" emit. */
const STUTTER_FRAME_THRESHOLD_MS = 200;
/** Min interval между stutter emits (anti-spam). */
const STUTTER_EMIT_THROTTLE_MS = 2000;

let installed = false;

export function installFpsMonitor(): void {
  if (installed) return;
  if (typeof window === 'undefined') return;
  if (typeof requestAnimationFrame === 'undefined') return;
  installed = true;

  const isMobile = window.matchMedia('(max-width: 600px), (pointer: coarse)').matches;
  const startedAt = performance.now();

  let lastFrameTs = performance.now();
  let frameTimes: number[] = [];
  let slowFrames = 0;
  let windowStartTs = performance.now();
  let lastStutterEmit = 0;
  let rafId: number | null = null;

  function tick(): void {
    const now = performance.now();
    const dt = now - lastFrameTs;
    lastFrameTs = now;

    // Skip первый frame (dt от startup'а большой и нерелевантен).
    if (frameTimes.length > 0 || dt < 100) {
      frameTimes.push(dt);
      if (dt >= SLOW_FRAME_THRESHOLD_MS) slowFrames += 1;

      // Серьёзный stutter — emit отдельно (но throttle).
      if (dt >= STUTTER_FRAME_THRESHOLD_MS && now - lastStutterEmit > STUTTER_EMIT_THROTTLE_MS) {
        emit('perf.slow_frame', {
          frame_ms: Math.round(dt),
          since_load_s: Math.round((now - startedAt) / 1000),
        });
        lastStutterEmit = now;
      }
    } else {
      // First frame — просто инициализация.
      frameTimes.push(dt);
    }

    // Каждые SAMPLE_WINDOW_MS — emit aggregate.
    if (now - windowStartTs >= SAMPLE_WINDOW_MS && frameTimes.length > 10) {
      const elapsedSec = (now - windowStartTs) / 1000;
      const sorted = [...frameTimes].sort((a, b) => a - b);
      const avgFrameMs = frameTimes.reduce((s, t) => s + t, 0) / frameTimes.length;
      const p95Index = Math.floor(sorted.length * 0.95);
      const p95FrameMs = sorted[p95Index] ?? avgFrameMs;
      const avgFps = avgFrameMs > 0 ? 1000 / avgFrameMs : 60;

      emit('perf.fps_sample', {
        avg_fps: Math.round(avgFps * 10) / 10,
        p95_frame_ms: Math.round(p95FrameMs * 10) / 10,
        slow_frames: slowFrames,
        sample_seconds: Math.round(elapsedSec * 10) / 10,
        is_mobile: isMobile,
        screen_w: window.innerWidth,
        screen_h: window.innerHeight,
      });

      // Reset window.
      frameTimes = [];
      slowFrames = 0;
      windowStartTs = now;
    }

    rafId = requestAnimationFrame(tick);
  }

  function start(): void {
    if (rafId !== null) return;
    lastFrameTs = performance.now();
    windowStartTs = lastFrameTs;
    frameTimes = [];
    slowFrames = 0;
    rafId = requestAnimationFrame(tick);
  }

  function stop(): void {
    if (rafId !== null) {
      cancelAnimationFrame(rafId);
      rafId = null;
    }
  }

  // Pause при скрытой странице — browser throttle'нет RAF до 1fps,
  // считать "лаги" в этом режиме = ложные сигналы.
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') start();
    else stop();
  });

  // Initial start (если страница уже видна).
  if (document.visibilityState === 'visible') start();
}

/**
 * Snapshot current FPS — для DebugBadge live display.
 * Возвращает avg за последние N frames в текущем окне.
 */
let _liveFps = 60;
let _liveP95 = 16.67;
export function getLiveFps(): { fps: number; p95FrameMs: number } {
  return { fps: _liveFps, p95FrameMs: _liveP95 };
}

// Update _liveFps inside the RAF tick — extending the closure above.
// Кладём здесь чтобы не загромождать tick:
function attachLiveSampling(): void {
  if (typeof window === 'undefined') return;
  let frames = 0;
  let last = performance.now();
  const samples: number[] = [];
  function update(): void {
    const now = performance.now();
    const dt = now - last;
    last = now;
    samples.push(dt);
    if (samples.length > 60) samples.shift();
    frames += 1;
    if (frames % 30 === 0 && samples.length > 0) {
      const avg = samples.reduce((s, t) => s + t, 0) / samples.length;
      _liveFps = avg > 0 ? Math.round(1000 / avg) : 60;
      const sorted = [...samples].sort((a, b) => a - b);
      _liveP95 = Math.round((sorted[Math.floor(sorted.length * 0.95)] ?? avg) * 10) / 10;
    }
    requestAnimationFrame(update);
  }
  requestAnimationFrame(update);
}
attachLiveSampling();
